
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, RefreshCw, Calculator } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import type { MarkschemeType, Question } from "@/types/test";
import { generateMarkscheme } from "@/components/test-creator/utils/questionGeneration";
import { AIGenerationWarningDialog } from "./AIGenerationWarningDialog";
import { MarkschemeHelpSection } from "./MarkschemeHelpSection";
import { EnhancedCriterionCard } from "./EnhancedCriterionCard";
import { MarkschemeContextDisplay } from "./MarkschemeContextDisplay";
import { MarkschemeEmptyState } from "./MarkschemeEmptyState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface MarkschemeDialogProps {
  questionNumber?: number;
  markscheme: MarkschemeType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (markscheme: MarkschemeType) => void;
  question?: Question;
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  };
}

export const MarkschemeDialog = ({
  questionNumber,
  markscheme,
  open,
  onOpenChange,
  onSave,
  question,
  jobContext,
}: MarkschemeDialogProps) => {
  const [isEditing, setIsEditing] = useState(!markscheme);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAIWarning, setShowAIWarning] = useState(false);
  const [formData, setFormData] = useState<MarkschemeType>(() => {
    if (markscheme) return markscheme;
    return {
      criteria: [{ name: "", description: "", max_points: 1 }],
      total_points: 1,
      overall_guidance: "",
      suggested_answer: "",
    };
  });

  const addCriterion = () => {
    setFormData(prev => ({
      ...prev,
      criteria: [
        ...(prev.criteria || []),
        { name: "", description: "", max_points: 1 }
      ]
    }));
  };

  const removeCriterion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      criteria: prev.criteria?.filter((_, i) => i !== index) || []
    }));
  };

  const updateCriterion = (index: number, field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      criteria: prev.criteria?.map((criterion, i) => 
        i === index ? { ...criterion, [field]: value } : criterion
      ) || []
    }));
  };

  const calculateTotalPoints = () => {
    const total = formData.criteria?.reduce((sum, criterion) => sum + criterion.max_points, 0) || 0;
    setFormData(prev => ({ ...prev, total_points: total }));
    return total;
  };

  const handleSave = () => {
    const total = calculateTotalPoints();
    onSave({ ...formData, total_points: total });
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (markscheme) {
      setFormData(markscheme);
      setIsEditing(false);
    } else {
      onOpenChange(false);
    }
  };

  const handleGenerateMarkscheme = async () => {
    if (!question) {
      toast({
        title: "Error",
        description: "Question data is required to generate a markscheme.",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    try {
      const generatedMarkscheme = await generateMarkscheme(question, jobContext);
      
      setFormData(generatedMarkscheme);
      calculateTotalPoints();
      
      toast({
        title: "Success",
        description: "Markscheme generated successfully!",
      });
    } catch (error) {
      console.error('Failed to generate markscheme:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      toast({
        title: "Generation Failed",
        description: errorMessage.includes('parse') 
          ? "The AI response was malformed. Please try again or create the markscheme manually."
          : errorMessage.includes('API')
          ? "API connection failed. Please check your connection and try again."
          : "Failed to generate markscheme. Please try again or create manually.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAIGenerateClick = () => {
    setShowAIWarning(true);
  };

  const handleStartManualCreation = () => {
    setIsEditing(true);
  };

  const totalPoints = formData.criteria?.reduce((sum, criterion) => sum + criterion.max_points, 0) || 0;
  const hasEmptyFields = formData.criteria?.some(c => !c.name.trim() || !c.description.trim()) || false;
  const questionContent = typeof question?.content === 'string' 
    ? JSON.parse(question.content) 
    : question?.content;
  const hasQuestionDetails = !!(questionContent?.question && question?.question_type);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto bg-background">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {questionNumber ? `Question ${questionNumber} Markscheme` : "Question Markscheme"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {!isEditing && !markscheme ? (
              // Empty State
              <div className="space-y-4">
                <MarkschemeContextDisplay question={question} jobContext={jobContext} />
                <MarkschemeEmptyState
                  onCreateManually={handleStartManualCreation}
                  onGenerateWithAI={handleAIGenerateClick}
                  hasQuestion={hasQuestionDetails}
                />
              </div>
            ) : !isEditing ? (
              // View Mode
              <div className="space-y-6">
                {markscheme?.criteria && markscheme.criteria.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-foreground">Assessment Criteria</h3>
                      <Badge variant="secondary" className="flex items-center gap-1 bg-secondary/10 text-secondary border-secondary/20">
                        <Calculator className="h-3 w-3" />
                        {markscheme.total_points} {markscheme.total_points === 1 ? 'point' : 'points'} total
                      </Badge>
                    </div>
                    
                    {markscheme.criteria.map((criterion, index) => (
                      <Card key={index} className="bg-card border-border rounded-sm shadow-sm">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold text-foreground">{criterion.name}</h4>
                            <Badge variant="outline" className="border-border text-muted-foreground">
                              {criterion.max_points} {criterion.max_points === 1 ? 'point' : 'points'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">{criterion.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="bg-card border-border rounded-sm shadow-sm">
                    <CardContent className="text-center py-8">
                      <p className="text-lg font-medium mb-2 text-foreground">No criteria defined</p>
                      <p className="text-sm text-muted-foreground">Edit this markscheme to add assessment criteria.</p>
                    </CardContent>
                  </Card>
                )}
                
                {markscheme?.overall_guidance && (
                  <Card className="bg-card border-border rounded-sm shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold text-foreground">Overall Guidance</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground leading-relaxed">{markscheme.overall_guidance}</p>
                    </CardContent>
                  </Card>
                )}
                
                {markscheme?.suggested_answer && (
                  <Card className="bg-card border-border rounded-sm shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold text-foreground">Suggested Answer</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{markscheme.suggested_answer}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              // Edit Mode
              <div className="space-y-6">
                <MarkschemeHelpSection />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Assessment Criteria</h3>
                    <Badge variant="secondary" className="flex items-center gap-1 bg-secondary/10 text-secondary border-secondary/20">
                      <Calculator className="h-3 w-3" />
                      {totalPoints} {totalPoints === 1 ? 'point' : 'points'} total
                    </Badge>
                  </div>
                  
                  <div className="space-y-4">
                    {formData.criteria?.map((criterion, index) => (
                      <EnhancedCriterionCard
                        key={index}
                        criterion={criterion}
                        index={index}
                        canDelete={formData.criteria && formData.criteria.length > 1}
                        onUpdate={updateCriterion}
                        onDelete={removeCriterion}
                      />
                    ))}
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={addCriterion}
                      className="flex-1 border-border hover:bg-muted"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Criterion
                    </Button>
                    {question && (
                      <Button
                        variant="outline"
                        onClick={handleAIGenerateClick}
                        disabled={isGenerating}
                        className="flex-1 border-border hover:bg-muted"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                        {isGenerating ? 'Generating...' : 'AI Generate'}
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label htmlFor="overall-guidance" className="text-sm font-medium text-foreground">
                      Overall Guidance
                    </Label>
                    <Textarea
                      id="overall-guidance"
                      value={formData.overall_guidance || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, overall_guidance: e.target.value }))}
                      placeholder="Provide general guidance for evaluators on how to assess this question consistently"
                      rows={3}
                      className="bg-card border-border rounded-sm focus:ring-secondary focus:border-secondary"
                    />
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="suggested-answer" className="text-sm font-medium text-foreground">
                      Suggested Answer
                    </Label>
                    <Textarea
                      id="suggested-answer"
                      value={formData.suggested_answer || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, suggested_answer: e.target.value }))}
                      placeholder="Provide a model answer or key points that should be covered in a good response"
                      rows={4}
                      className="bg-card border-border rounded-sm focus:ring-secondary focus:border-secondary"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            {!isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditing(true)}
                  className="border-border hover:bg-muted"
                >
                  Edit
                </Button>
                <DialogClose asChild>
                  <Button className="bg-secondary hover:bg-secondary-hover text-secondary-foreground relative overflow-hidden before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-gradient-to-r before:from-white/0 before:via-white/20 before:to-white/0 before:translate-x-[-100%] hover:before:animate-gloss">
                    Close
                  </Button>
                </DialogClose>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  className="border-border hover:bg-muted"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={hasEmptyFields}
                  className={`bg-secondary hover:bg-secondary-hover text-secondary-foreground relative overflow-hidden before:absolute before:top-0 before:left-0 before:w-full before:h-full before:bg-gradient-to-r before:from-white/0 before:via-white/20 before:to-white/0 before:translate-x-[-100%] hover:before:animate-gloss ${
                    hasEmptyFields ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {hasEmptyFields ? 'Complete Required Fields' : 'Save Markscheme'}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AIGenerationWarningDialog
        open={showAIWarning}
        onOpenChange={setShowAIWarning}
        onConfirm={handleGenerateMarkscheme}
        question={question}
        jobContext={jobContext}
      />
    </>
  );
};
